import React, { useEffect, useRef, useState } from "react";
import { Moon, Sun, Globe, LogOut, Camera, KeyRound, Bell, Download, Eye, EyeOff, Loader2, Mail, MailCheck, Clock } from "lucide-react";
import Modal from "@/components/chat/Modal";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { getInitials, avatarColor } from "@/lib/avatar";
import { enableWebPush } from "@/lib/push";
import { toast } from "sonner";
import { uploadAvatar } from "@/lib/uploads";

export default function SettingsDialog({ onClose }) {
  const { user, logout, refreshUser, setUser } = useAuth();
  const { theme, toggle } = useTheme();
  const { lang, setLang } = useI18n();
  const { canInstall, installed, isIOS, promptInstall } = usePWAInstall();
  const [form, setForm] = useState({ name: user.name || "", country: user.country || "", flag: user.flag || "", about: user.about || "", avatar: user.avatar || null });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings/notifications").then(({ data }) => setNotif(data)).catch(() => {});
  }, []);

  const updateNotif = async (patch) => {
    const next = { ...notif, ...patch };
    setNotif(next);
    try { await api.put("/settings/notifications", patch); }
    catch (e) { toast.error("Could not save"); }
  };

  const previewEmail = async () => {
    try {
      const { data } = await api.post("/settings/notifications/preview");
      toast.success(data.delivered ? "Preview email sent to your inbox 📬" : "Preview logged (see note below)");
      setNotif((n) => ({ ...n, _preview: data }));
    } catch (e) { toast.error("Preview failed"); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { const url = await uploadAvatar(file); setForm((f) => ({ ...f, avatar: url })); }
    catch { toast.error("Could not upload photo"); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try { const { data } = await api.put("/auth/profile", form); setUser(data); toast.success("Profile updated"); }
    catch (e) { toast.error("Update failed"); } finally { setSaving(false); }
  };

  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) return;
    try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const enablePush = async () => {
    try {
      const r = await enableWebPush();
      if (r.ok) toast.success("Push notifications enabled");
      else if (r.reason === "denied") toast.error("Notifications blocked");
      else if (r.reason === "ios-not-installed") toast("On iPhone: tap Share → Add to Home Screen, then open RoyalChat Nova from your Home Screen to enable notifications.", { duration: 8000 });
      else toast.error("Not supported on this device");
    } catch (e) { toast.error("Could not enable push"); }
  };

  const install = async () => {
    if (installed) return toast.success("Already installed");
    if (canInstall) return void promptInstall();
    toast(isIOS ? "Tap Share → Add to Home Screen" : "Use browser menu → Install app", { duration: 6000 });
  };

  const field = "w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-2xl px-4 py-3 focus:outline-none focus:border-[var(--rc-gold)]/50";

  return (
    <Modal title="Settings" onClose={onClose}>
      {/* Profile */}
      <div className="flex flex-col items-center mb-5">
        <button onClick={() => fileRef.current?.click()} className="relative group" data-testid="avatar-upload-btn">
          <div className="h-20 w-20 rounded-full grid place-items-center text-[var(--rc-gold)] text-2xl font-heading overflow-hidden border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(form.name) }}>
            {form.avatar ? <img src={form.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(form.name)}
          </div>
          <span className="absolute bottom-0 right-0 h-7 w-7 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black"><Camera size={14} /></span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} />
        <p className="text-xs text-[var(--rc-muted)] mt-2">{user.email} · {user.role}</p>
      </div>

      <div className="space-y-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={field} data-testid="settings-name-input" />
        <div className="grid grid-cols-2 gap-3">
          <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country" className={field} />
          <input value={form.flag} onChange={(e) => setForm({ ...form, flag: e.target.value })} placeholder="Flag 🇷🇼" className={field} />
        </div>
        <input value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="About" className={field} />
        <button onClick={saveProfile} disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[var(--rc-gold)] text-black font-semibold py-3" data-testid="save-profile-btn">
          {saving && <Loader2 size={16} className="animate-spin" />} Save profile
        </button>
      </div>

      {/* Preferences */}
      <div className="mt-6 space-y-2">
        <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-[var(--rc-bg)]" data-testid="theme-toggle-btn">
          <span className="flex items-center gap-2">{theme === "dark" ? <Moon size={18} /> : <Sun size={18} />} Theme</span>
          <span className="text-[var(--rc-gold)] capitalize">{theme}</span>
        </button>
        <div className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-[var(--rc-bg)]">
          <span className="flex items-center gap-2"><Globe size={18} /> Language</span>
          <div className="flex flex-wrap gap-1 justify-end max-w-[70%]">
            {LANGS.map((L) => (
              <button key={L.code} onClick={() => setLang(L.code)} data-testid={`lang-${L.code}`} className={`px-2.5 py-1 rounded-full text-sm ${lang === L.code ? "bg-[var(--rc-gold)] text-black" : "text-[var(--rc-muted)]"}`}>{L.flag}</button>
            ))}
          </div>
        </div>
        <button onClick={enablePush} className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl bg-[var(--rc-bg)]" data-testid="enable-push-btn"><Bell size={18} /> Enable push notifications</button>
        <button onClick={install} className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl bg-[var(--rc-bg)]" data-testid="settings-install-btn"><Download size={18} /> {installed ? "App installed" : "Install RoyalChat Nova"}</button>
      </div>

      {/* Away-email alerts */}
      {notif && (
        <div className="mt-6">
          <p className="text-sm font-medium mb-2 flex items-center gap-2"><Mail size={16} /> Away-email alerts</p>
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm">Email me when I'm offline</div>
                <div className="text-xs text-[var(--rc-muted)]">Sent to {notif.email} · ~15-min throttle</div>
              </div>
              <button onClick={() => updateNotif({ away_email_enabled: !notif.away_email_enabled })} data-testid="away-email-toggle"
                className={`relative h-6 w-11 rounded-full transition-colors ${notif.away_email_enabled ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-surface-2)]"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${notif.away_email_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-sm flex items-center gap-1.5"><Clock size={14} /> Quiet hours</div>
              <button onClick={() => updateNotif({ quiet_hours_enabled: !notif.quiet_hours_enabled })} data-testid="quiet-hours-toggle"
                className={`relative h-6 w-11 rounded-full transition-colors ${notif.quiet_hours_enabled ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-surface-2)]"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${notif.quiet_hours_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {notif.quiet_hours_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <input type="time" value={notif.quiet_start} onChange={(e) => updateNotif({ quiet_start: e.target.value })} className="bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm" data-testid="quiet-start" />
                <input type="time" value={notif.quiet_end} onChange={(e) => updateNotif({ quiet_end: e.target.value })} className="bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm" data-testid="quiet-end" />
              </div>
            )}
            <button onClick={previewEmail} className="w-full flex items-center justify-center gap-2 rounded-xl border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] py-2 text-sm" data-testid="preview-email-btn"><MailCheck size={15} /> Send me a preview</button>
            {notif._preview && (
              <div className="rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-3 text-xs">
                <div className="text-[var(--rc-gold)] font-medium mb-1">{notif._preview.subject}</div>
                <div className="text-[var(--rc-muted)] whitespace-pre-wrap">{notif._preview.body}</div>
                {notif._preview.note && <div className="mt-2 text-[10px] text-[var(--rc-muted)] italic">{notif._preview.note}</div>}
              </div>
            )}
            <p className="text-[10px] text-[var(--rc-muted)]">Emails are delivered via Resend. Until you verify your own domain, Resend only delivers to your account's verified address; other recipients are safely logged.</p>
          </div>
        </div>
      )}

      {/* Change password */}
      <div className="mt-6">
        <p className="text-sm font-medium mb-2 flex items-center gap-2"><KeyRound size={16} /> Change password</p>
        <div className="space-y-2">
          <div className="relative">
            <input type={showPw ? "text" : "password"} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Current password" className={field} data-testid="current-pw-input" />
            <button onClick={() => setShowPw((s) => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          </div>
          <input type={showPw ? "text" : "password"} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="New password" className={field} data-testid="new-pw-input" />
          <button onClick={changePw} className="w-full rounded-2xl border border-[var(--rc-gold)] text-[var(--rc-gold)] font-semibold py-2.5" data-testid="change-pw-btn">Update password</button>
        </div>
      </div>

      <button onClick={logout} className="w-full mt-6 flex items-center justify-center gap-2 rounded-2xl bg-red-500/10 text-red-400 border border-red-500/30 py-3 font-medium" data-testid="logout-btn">
        <LogOut size={18} /> Log out
      </button>
    </Modal>
  );
}
